import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiExternalLink, FiPower, FiTrash2, FiUsers, FiMoreVertical, FiTerminal, FiClock, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { panelsApi } from '../../api';
import toast from 'react-hot-toast';
import DeploymentLogsModal from './DeploymentLogsModal';

export default function PanelList({ panels, onRefresh }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [panelToDelete, setPanelToDelete] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [viewingLogsPanel, setViewingLogsPanel] = useState(null);
  const [expandedChildren, setExpandedChildren] = useState({});
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuRef]);

  // Auto refresh if any panel is deploying
  useEffect(() => {
    const hasDeploying = panels.some(p => p.status === 'deploying' || p.status === 'pending');
    if (hasDeploying) {
      const interval = setInterval(() => {
        onRefresh(true);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [panels, onRefresh]);

  const filtered = panels.filter(p => {
    const s = search.toLowerCase();
    const matchesSearch = (p.panel_name || p.domain || '').toLowerCase().includes(s) || p.domain.toLowerCase().includes(s);
    const matchesStatus = !statusFilter || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleApprove = async (id) => {
    try {
      toast.loading('Approving order & starting provisioning...', { id: 'approve' });
      await panelsApi.approve(id);
      toast.success('Panel approved! Provisioning started.', { id: 'approve' });
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve panel', { id: 'approve' });
    }
  };

  const handleReject = async (id) => {
    try {
      await panelsApi.reject(id);
      toast.success('Panel order rejected');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reject panel');
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await panelsApi.toggle(id);
      toast.success(currentStatus === 'active' ? 'Panel suspended' : 'Panel activated');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to toggle');
    }
    setOpenMenuId(null);
  };

  const handleDelete = async (panel) => {
    try {
      toast.loading('Initiating server wipe...', { id: 'delete' });
      await panelsApi.delete(panel.id);
      toast.success('Wipe process started', { id: 'delete' });
      setViewingLogsPanel({
        ...panel,
        status: 'deleting',
        deploy_log: `[${new Date().toISOString()}] Initiating panel deletion...\n`
      });
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete', { id: 'delete' });
    }
  };

  // mode 'readonly' (default) opens the panel for looking; 'full' is the
  // logged unlock for when something actually needs changing.
  const handleLoginAsAdmin = async (id, domain, mode = 'readonly') => {
    try {
      const { data } = await panelsApi.loginAsAdmin(id, mode);
      toast.success(mode === 'full'
        ? `Opening ${domain} with full access...`
        : `Opening ${domain} — read only...`);
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to log in to panel');
    }
    setOpenMenuId(null);
  };

  const handleRenew = async (id) => {
    try {
      await panelsApi.renew(id);
      toast.success('Panel renewed for 30 days');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to renew');
    }
    setOpenMenuId(null);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            type="text"
            placeholder="Filter panels..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-background border border-border text-foreground rounded-md pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-all placeholder:text-muted-foreground"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-background border border-border text-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-all min-w-[140px] max-w-[180px]"
        >
          <option value="">All Statuses</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="pending">Pending</option>
          <option value="deploying">Deploying</option>
          <option value="rejected">Rejected</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-visible">
        <div className="overflow-x-auto min-h-[300px] pb-32">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Panel</th>
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Status</th>
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Created</th>
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Expiry</th>
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-right">Orders</th>
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-right">Revenue</th>
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {filtered.map(panel => (
                <Fragment key={panel.id}>
                <tr className={`border-b border-border hover:bg-muted/50 transition-colors ${openMenuId === panel.id ? 'relative z-50 bg-muted/50' : 'relative z-0'}`}>
                  <td className="p-4 align-middle">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        {panel.children?.length > 0 && (
                          <button onClick={() => setExpandedChildren(prev => ({ ...prev, [panel.id]: !prev[panel.id] }))}
                            className="p-0.5 text-muted-foreground hover:text-foreground rounded transition-colors" title={`${panel.children.length} child panel(s)`}>
                            {expandedChildren[panel.id] ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                          </button>
                        )}
                        <span className="font-medium text-foreground">{panel.panel_name || panel.domain}</span>
                        {panel.children?.length > 0 && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-violet-500/10 text-violet-600 border border-violet-200">
                            {panel.children.length} Child
                          </span>
                        )}
                        {panel.creator === 'website' && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-500/10 text-blue-600 border border-blue-200">
                            Website Order
                          </span>
                        )}
                      </div>
                      <a href={`https://${panel.domain}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground text-xs flex items-center gap-1 transition-colors w-fit">
                        {panel.domain} <FiExternalLink size={10} />
                      </a>
                      {(panel.user_email || panel.user_username) && (
                        <span className="text-[11px] text-blue-600 font-medium">
                          User: {panel.user_email || panel.user_username}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 align-middle">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                      panel.status === 'active' ? 'bg-secondary text-secondary-foreground border-transparent' : 
                      panel.status === 'pending_approval' ? 'bg-amber-500/10 text-amber-600 border-amber-300 font-semibold' :
                      panel.status === 'rejected' ? 'bg-red-500/10 text-red-600 border-red-300 font-semibold' :
                      panel.status === 'suspended' ? 'bg-muted text-muted-foreground border-transparent' : 
                      panel.status === 'pending' || panel.status === 'deploying' ? 'bg-secondary text-secondary-foreground border-transparent' : 
                      'bg-destructive/10 text-destructive border-transparent'
                    }`}>
                      {panel.status === 'pending_approval' ? 'Pending Approval' : panel.status.charAt(0).toUpperCase() + panel.status.slice(1)}
                    </span>
                  </td>
                  <td className="p-4 align-middle text-muted-foreground text-xs">
                    {panel.created_at ? new Date(panel.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                  </td>
                  <td className="p-4 align-middle">
                    {panel.expires_at ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                        new Date(panel.expires_at) < new Date() 
                          ? 'bg-destructive/10 text-destructive border-transparent' 
                          : 'bg-secondary/40 text-secondary-foreground/80 border-transparent'
                      }`}>
                        {new Date(panel.expires_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="p-4 align-middle text-muted-foreground font-medium text-right">
                    {panel.total_orders?.toLocaleString() || 0}
                  </td>
                  <td className="p-4 align-middle text-foreground font-medium text-right">
                    <div className="flex flex-col items-end">
                      {panel.total_revenue_usd && Number(panel.total_revenue_usd) > 0 ? (
                        <span>${Number(panel.total_revenue_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      ) : (
                        <span>
                          {panel.currency === 'USD' || !panel.currency ? '$' : ''}
                          {Number(panel.total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          {panel.currency && panel.currency !== 'USD' ? ` ${panel.currency}` : ''}
                        </span>
                      )}
                      {panel.total_revenue_usd && Number(panel.total_revenue_usd) > 0 && panel.currency && panel.currency !== 'USD' && (
                        <span className="text-xs text-muted-foreground font-normal">
                          {Number(panel.total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {panel.currency}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 align-middle text-right relative">
                    {panel.status === 'pending_approval' ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleApprove(panel.id)}
                          className="px-2.5 py-1 text-xs font-semibold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(panel.id)}
                          className="px-2.5 py-1 text-xs font-semibold rounded-md bg-rose-600 hover:bg-rose-700 text-white shadow-sm transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <>
                        <button 
                          onClick={() => setOpenMenuId(openMenuId === panel.id ? null : panel.id)}
                          className="p-2 text-muted-foreground hover:text-foreground rounded-md transition-colors inline-flex"
                        >
                          <FiMoreVertical size={16} />
                        </button>
                        
                        {openMenuId === panel.id && (
                          <div ref={menuRef} className="absolute right-6 top-10 w-48 bg-popover text-popover-foreground rounded-md shadow-md border border-border z-50 py-1 text-sm animate-in fade-in slide-in-from-top-1">
                            <button onClick={() => navigate(`/panels/${panel.id}/admins`)} className="w-full text-left px-3 py-1.5 hover:bg-muted flex items-center gap-2">
                              <FiUsers size={14} className="text-muted-foreground" /> Manage Accounts
                            </button>
                            {panel.status === 'active' && (
                              <>
                                <button onClick={() => handleLoginAsAdmin(panel.id, panel.domain)} className="w-full text-left px-3 py-1.5 hover:bg-muted flex items-center gap-2">
                                  <FiExternalLink size={14} className="text-muted-foreground" /> Login (read only)
                                </button>
                                <button onClick={() => handleLoginAsAdmin(panel.id, panel.domain, 'full')} className="w-full text-left px-3 py-1.5 hover:bg-muted flex items-center gap-2">
                                  <FiExternalLink size={14} className="text-muted-foreground" /> Login with full access
                                </button>
                              </>
                            )}
                            <button onClick={() => { setViewingLogsPanel(panel); setOpenMenuId(null); }} className="w-full text-left px-3 py-1.5 hover:bg-muted flex items-center gap-2 border-b border-border mb-1 pb-2">
                              <FiTerminal size={14} className="text-muted-foreground" /> View Live Logs
                            </button>
                            
                            {panel.status === 'pending' && (
                              <button onClick={() => { 
                                panelsApi.retry(panel.id)
                                  .then(() => toast.success('Retry initiated!'))
                                  .catch(() => toast.error('Failed to retry'));
                                onRefresh(); 
                                setOpenMenuId(null); 
                              }} className="w-full text-left px-3 py-1.5 text-secondary-foreground hover:bg-muted flex items-center gap-2 font-medium mb-1">
                                <FiPower size={14} /> Retry Deployment
                              </button>
                            )}

                            <button onClick={() => handleToggle(panel.id, panel.status)} className="w-full text-left px-3 py-1.5 hover:bg-muted flex items-center gap-2">
                              <FiPower size={14} className="text-muted-foreground" /> {panel.status === 'active' ? 'Suspend Panel' : 'Activate Panel'}
                            </button>

                            <button onClick={() => handleRenew(panel.id)} className="w-full text-left px-3 py-1.5 hover:bg-muted flex items-center gap-2">
                              <FiClock size={14} className="text-muted-foreground" /> Renew (30 Days)
                            </button>
                            
                            <button onClick={() => { setPanelToDelete(panel); setOpenMenuId(null); }} className="w-full text-left px-3 py-1.5 text-destructive hover:bg-destructive/10 flex items-center gap-2 mt-1">
                              <FiTrash2 size={14} /> Delete Panel
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </td>
                </tr>
                {expandedChildren[panel.id] && panel.children?.length > 0 && (
                  <tr className="border-b border-border bg-muted/20">
                    <td colSpan={7} className="p-4 pl-12">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        Child Panels ({panel.children.length})
                      </div>
                      <div className="space-y-2">
                        {panel.children.map(child => (
                          <div key={child.id} className="flex items-center justify-between py-2 px-3 rounded-md bg-background border border-border">
                            <div className="flex items-center gap-3 flex-wrap">
                              <a href={`https://${child.domain}`} target="_blank" rel="noreferrer" className="text-sm font-medium text-foreground hover:underline flex items-center gap-1">
                                {child.panel_name || child.domain} <FiExternalLink size={10} className="text-muted-foreground" />
                              </a>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                                child.status === 'active' ? 'bg-secondary text-secondary-foreground border-transparent' :
                                child.status === 'pending' || child.status === 'deploying' ? 'bg-secondary text-secondary-foreground border-transparent' :
                                child.status === 'suspended' ? 'bg-muted text-muted-foreground border-transparent' :
                                'bg-destructive/10 text-destructive border-transparent'
                              }`}>
                                {child.status.charAt(0).toUpperCase() + child.status.slice(1)}
                              </span>
                              {child.expires_at && (
                                <span className="text-xs text-muted-foreground">
                                  Expires {new Date(child.expires_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                </span>
                              )}
                            </div>
                            <button onClick={() => navigate(`/panels/${child.id}`)} className="px-2.5 py-1 text-xs font-semibold rounded-md border border-border hover:bg-muted transition-colors">
                              Manage
                            </button>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    No panels found. {search || statusFilter ? 'Try adjusting your filters.' : 'Click "Create Panel" to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewingLogsPanel && (
        <DeploymentLogsModal 
          panel={viewingLogsPanel} 
          onClose={() => setViewingLogsPanel(null)} 
        />
      )}

      {panelToDelete && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-card border border-border text-card-foreground rounded-lg max-w-md w-full p-6 shadow-lg animate-in fade-in zoom-in-95 duration-200">
            <div className="flex flex-col space-y-2 mb-4">
              <h2 className="text-lg font-semibold tracking-tight">Are you absolutely sure?</h2>
              <p className="text-sm text-muted-foreground">
                This action cannot be undone. You are about to permanently delete <strong className="text-foreground">{panelToDelete.domain}</strong>.
              </p>
            </div>
            
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 mb-6">
              <p className="text-sm text-destructive font-medium mb-2">This will irreversibly wipe from the server:</p>
              <ul className="list-disc list-inside text-sm text-destructive/80 space-y-1">
                <li>The Website in aaPanel</li>
                <li>All files and folders on the server</li>
                <li>The MySQL Database and all data</li>
                <li>SSL Certificates and Nginx configurations</li>
              </ul>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setPanelToDelete(null)} className="h-9 px-4 py-2 rounded-md border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors">Cancel</button>
              <button onClick={() => { handleDelete(panelToDelete); setPanelToDelete(null); }} className="h-9 px-4 py-2 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground text-sm font-medium transition-colors">Permanently Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
